import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs';
import queryString from 'query-string';
import attempt from 'lodash/attempt';
import { isNaN, isFinite } from 'lodash';

import * as ActionCreators from '../../actions/ActionCreators';
import * as ActionTypes from '../../actions/ActionTypes';
import { LOCALSTORAGE_MUTED } from '../../constants';



// if there's a muted state set to true in localstorage, mute player on init
const setInitialMuteStateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.INIT)
    .map(() => attempt(() => !!JSON.parse(window.localStorage.getItem(LOCALSTORAGE_MUTED))))
    .filter(muted => !!muted)
    .do(muted => player.muted(muted))
    .ignoreElements();


const setInitialVideoIDEpic = (action$, __, { options }) => action$
    .ofType(ActionTypes.INIT)
    .filter(() => !!options.videoID)
    .map(() => options)
    .pluck('videoID')
    .map(videoID => ActionCreators.setVideoID(videoID));


// enable or disable ads based on inital state of ads dependency
const setInitialAdsStateEpic = (action$, __, { ads }) => action$
    .ofType(ActionTypes.INIT)
    .map(() => ads)
    .switchMap(ads => Observable.of(
        (!!ads) ? ActionCreators.enableAds() : ActionCreators.disableAds()
    ));


const setFirstTimeStateEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_PLAYING)
    .filter(() => !store.getState().player.adPlaying) // only when ad isn't playing
    .filter(() => store.getState().player.firstTime === null) // only set if first time is null
    .map(() => player.currentTime())
    .map(currentTime => ActionCreators.setFirstTimeState(currentTime)); // get current time and set it to first time


// sets the video started state
// only applies when content starts, not ads
const setStartedStateEpic = (action$, store, { }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_PLAYING)
    .filter(() => store.getState().player.started === null) // only set if first time is null
    .filter(() => !store.getState().player.adPlaying) // only when ad isn't playing
    .map(() => ActionCreators.setStartedState()); // get current time and set it to first time


const setCurrentTimeEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_TIME_UPDATE)
    .map(() => player.currentTime())
    .map(currentTime => ActionCreators.setCurrentTimeState(currentTime));


const setPlayingStateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_PLAYING)
    .map(() => player.paused())
    .switchMap(paused => Observable.of(
        ActionCreators.setPlayingState(!paused),
        ActionCreators.setPauseState(paused),
    ));


const setPauseStateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_PAUSE)
    .map(() => player.paused())
    .switchMap(paused => Observable.of(
        ActionCreators.setPauseState(paused),
        ActionCreators.setPlayingState(!paused)
    ));


// save the duration to the state
const setDurationStateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_DURATION_CHANGE)
    .map(() => player.duration())
    .filter(duration => !isNaN(duration))
    .map(duration => ActionCreators.setDurationState(duration));


const setVolumeStateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_VOLUME_CHANGE)
    .map(() => player.muted()) // get mute state from player
    .distinctUntilChanged() // stop here if the muted state hasn't changed
    .do(muted => window.localStorage.setItem(LOCALSTORAGE_MUTED, muted)) // set the local storage mute state
    .switchMap(muted => Observable.of(
        ActionCreators.setMuteState(muted),
        (muted) ? ActionCreators.setVolumeState(0) : ActionCreators.setVolumeState(player.volume())
    ));


const setVideoProgressStateEpic = (action$, store, { }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_TIME_UPDATE)
    .throttleTime(1000)
    .filter(() => !store.getState().player.adPlaying) // only when ad isn't playing
    .map(() => ActionCreators.setVideoProgressState(store.getState().player.videoProgress += 1)); // add 1 to the seconds or time watched


// set live state to the hls stream type
const setLiveStateFromStream = (action$, __, { }) => action$
    .ofType(ActionTypes.HLS_EVENT_LEVEL_LOADED)
    .pluck('payload', 'details', 'live')
    .map(isLive => ActionCreators.setLiveState(isLive));


// Save the error text from from the player to the state
const setErrorStateEpic = (action$, __, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_ERROR)
    .map(() => ActionCreators.setErrorState(player.error()));


// handle query timecode
const handleQueryParamTimecodeEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_CAN_PLAY_THROUGH)
    .filter(() => !!options.queryParamTimecode) // when query param timecode seeking is turned on
    .filter(() => !store.getState().player.queryParamTimecodeSeeked) // when timecode hasn't already been seeked to
    .filter(() => !store.getState().player.adPlaying) // when an ad is not playing
    .map(() => queryString.parse(location.search))
    .map(queryParams => parseInt(queryParams.t)) // map to timecode
    .filter(timecode => !isNaN(timecode)) // if not a number, stop
    .filter(() => isFinite(player.duration())) // if player is a live stream, stop
    .do(timecode => player.currentTime(timecode)) // seek to the time
    .map(() => ActionCreators.setQueryParamTimecodeSeekedState(true)); // save muted to state and fire event


export default combineEpics(
    handleQueryParamTimecodeEpic,
    setCurrentTimeEpic,
    setDurationStateEpic,
    setErrorStateEpic,
    setFirstTimeStateEpic,
    setInitialAdsStateEpic,
    setInitialMuteStateEpic,
    setInitialVideoIDEpic,
    setLiveStateFromStream,
    setPauseStateEpic,
    setPlayingStateEpic,
    setStartedStateEpic,
    setVideoProgressStateEpic,
    setInitialVideoIDEpic,
    setVolumeStateEpic,
);


// WEBPACK FOOTER //
// ./src/player/state/epics.js